// arise-frontend/src/components/account/AccountPanel.jsx
// Plan / usage summary, job history, and the quota-exhausted choices.
// Everything shown comes from backend responses; nothing is simulated.
import { useCallback, useEffect, useState } from 'react';
import {
  getBillingPlans, getEntitlements, getUsage, listJobs, formatPrice,
} from '../../services/api';

const mono = { fontFamily: 'Space Mono, monospace' };
const card = {
  background: 'rgba(4,12,28,0.97)', border: '1px solid rgba(0,240,255,0.18)',
  borderRadius: 8, padding: '1.25rem',
};
const label = { ...mono, fontSize: 9, color: '#6b8fa8', textTransform: 'uppercase', letterSpacing: 1 };
const value = { fontFamily: 'Rajdhani, sans-serif', fontSize: 20, color: '#e0f4ff', fontWeight: 600 };

const STATUS_STYLE = {
  completed:  { text: 'Completed',  color: '#00ff88' },
  processing: { text: 'Running',    color: '#00f0ff' },
  queued:     { text: 'Queued',     color: '#ffd166' },
  uploaded:   { text: 'Waiting',    color: '#ffd166' },
  failed:     { text: 'Failed',     color: '#ff3c6e' },
};

const SOURCE_TEXT = { subscription: 'Subscription', free_quota: 'Free quota', credit: 'Credit' };

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function useLoad(loader, deps) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const load = useCallback(() => {
    const ctrl = new AbortController();
    setState(s => ({ ...s, status: 'loading', error: null }));
    loader(ctrl.signal).then(
      data => setState({ status: 'ready', data, error: null }),
      err => { if (!err || err.name !== 'AbortError') setState({ status: 'error', data: null, error: err }); },
    );
    return () => ctrl.abort();
  }, deps);
  useEffect(() => load(), [load]);
  return [state, load];
}

function LoadError({ what, onRetry }) {
  return (
    <div role="alert" style={{ ...mono, fontSize: 10, color: '#ff3c6e' }}>
      Could not load {what}.{' '}
      <button type="button" className="btn-ghost" style={{ fontSize: 9, padding: '2px 10px' }} onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/* ── Plan & usage ─────────────────────────────────────────────────────────── */
export function PlanSummary({ refreshKey }) {
  const [ent, reloadEnt] = useLoad(getEntitlements, [refreshKey]);
  const [usage, reloadUsage] = useLoad(getUsage, [refreshKey]);

  return (
    <section aria-labelledby="plan-heading" style={card}>
      <h2 id="plan-heading" style={{ ...label, fontSize: 10, color: '#00f0ff', margin: '0 0 1rem' }}>
        Plan &amp; usage
      </h2>
      {ent.status === 'loading' && <div style={{ ...mono, fontSize: 10, color: '#6b8fa8' }} role="status">Loading plan…</div>}
      {ent.status === 'error' && <LoadError what="your plan" onRetry={reloadEnt} />}
      {ent.status === 'ready' && ent.data && <PlanDetails ent={ent.data} />}

      <h3 style={{ ...label, margin: '1.25rem 0 0.5rem' }}>Recent usage</h3>
      {usage.status === 'loading' && <div style={{ ...mono, fontSize: 10, color: '#6b8fa8' }} role="status">Loading usage…</div>}
      {usage.status === 'error' && <LoadError what="usage history" onRetry={reloadUsage} />}
      {usage.status === 'ready' && (usage.data.length === 0
        ? <div style={{ ...mono, fontSize: 10, color: '#6b8fa8' }}>No analyses charged yet.</div>
        : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {usage.data.slice(0, 5).map(u => (
              <li key={u.job_id} style={{ ...mono, fontSize: 10, color: '#a0c4d8', display: 'flex', gap: 8, flexWrap: 'wrap', padding: '2px 0' }}>
                <span>{fmtDate(u.created_at)}</span>
                <span>· {SOURCE_TEXT[u.source] || u.source}</span>
                {u.status === 'refunded' && <span style={{ color: '#00ff88' }}>· refunded</span>}
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

function PlanDetails({ ent }) {
  const fq = ent.free_quota || {};
  const sub = ent.subscription;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
      <div>
        <div style={label}>Plan</div>
        <div style={value} data-testid="plan-name">
          {sub ? `Monthly (${sub.plan})` : (ent.tier ? ent.tier.charAt(0).toUpperCase() + ent.tier.slice(1) : '—')}
        </div>
        {sub && (
          <div style={{ ...mono, fontSize: 9, color: '#6b8fa8' }}>
            {sub.cancel_at_period_end ? 'Ends' : 'Renews/ends'} {fmtDate(sub.period_end)}
            {' · '}{sub.remaining == null ? 'unlimited' : `${sub.remaining} left`}
          </div>
        )}
      </div>
      <div>
        <div style={label}>Free monthly quota</div>
        <div style={value} data-testid="free-quota">
          {fq.limit == null ? 'Unlimited' : `${fq.remaining} of ${fq.limit} left`}
        </div>
        <div style={{ ...mono, fontSize: 9, color: '#6b8fa8' }}>Resets {fmtDate(fq.resets_at)}</div>
      </div>
      <div>
        <div style={label}>Analysis credits</div>
        <div style={value} data-testid="credits">{Number(ent.credits) || 0}</div>
      </div>
    </div>
  );
}

/* ── Job history ──────────────────────────────────────────────────────────── */
export function JobHistory({ refreshKey, onOpen }) {
  const [jobs, reload] = useLoad(listJobs, [refreshKey]);

  return (
    <section aria-labelledby="jobs-heading" style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h2 id="jobs-heading" style={{ ...label, fontSize: 10, color: '#00f0ff', margin: 0 }}>Your analyses</h2>
        <button type="button" className="btn-ghost" style={{ fontSize: 9, padding: '2px 10px' }} onClick={reload}>
          Refresh
        </button>
      </div>
      {jobs.status === 'loading' && <div style={{ ...mono, fontSize: 10, color: '#6b8fa8' }} role="status">Loading analyses…</div>}
      {jobs.status === 'error' && <LoadError what="your analyses" onRetry={reload} />}
      {jobs.status === 'ready' && (jobs.data.length === 0
        ? <div style={{ ...mono, fontSize: 10, color: '#6b8fa8' }}>No analyses yet. Upload a file to start.</div>
        : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {jobs.data.map(job => {
              const st = STATUS_STYLE[job.status] || { text: job.status || 'Unknown', color: '#6b8fa8' };
              const canOpen = job.status === 'completed' || job.status === 'processing'
                || job.status === 'queued' || job.status === 'uploaded';
              return (
                <li key={job.job_id} data-testid="job-row" style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '0.5rem 0', borderTop: '1px solid rgba(0,240,255,0.08)',
                }}>
                  <span style={{ ...mono, fontSize: 11, color: '#e0f4ff', flex: '1 1 160px', wordBreak: 'break-all' }}>
                    {job.filename || 'Unnamed file'}
                  </span>
                  <span style={{ ...mono, fontSize: 9, color: '#6b8fa8' }}>{fmtDate(job.created_at)}</span>
                  <span style={{ ...mono, fontSize: 9, color: st.color }}>
                    {st.text}{job.status === 'processing' && job.stage ? ` · ${job.stage.replace(/_/g, ' ')}` : ''}
                  </span>
                  {job.status === 'failed' && job.error && (
                    <span style={{ ...mono, fontSize: 9, color: '#ff8fa8', flexBasis: '100%' }}>{job.error}</span>
                  )}
                  {canOpen && (
                    <button type="button" className="btn-primary" style={{ fontSize: 9, padding: '4px 12px' }}
                      onClick={() => onOpen(job)}
                      aria-label={`${job.status === 'completed' ? 'View results for' : 'Track'} ${job.filename || 'analysis'}`}>
                      {job.status === 'completed' ? 'View' : 'Track'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
    </section>
  );
}

/* ── Quota exhausted ──────────────────────────────────────────────────────── */
export function QuotaExhausted({ message, onBack }) {
  const [plans] = useLoad(getBillingPlans, []);
  const cfg = plans.status === 'ready' ? plans.data : null;
  const single = cfg && cfg.single_analysis;
  const monthly = cfg && cfg.plans && cfg.plans.monthly;
  const singlePrice = single ? formatPrice(single.price_minor, cfg.currency) : null;
  const monthlyPrice = monthly ? formatPrice(monthly.price_minor, cfg.currency) : null;

  const option = (title, price, detail, testId) => (
    <div style={{ ...card, flex: '1 1 220px' }} data-testid={testId}>
      <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 13, color: '#e0f4ff', marginBottom: 6 }}>{title}</div>
      {price && <div style={{ ...value, color: '#00f0ff' }}>{price}</div>}
      <div style={{ ...mono, fontSize: 10, color: '#a0c4d8', margin: '0.5rem 0 1rem' }}>{detail}</div>
      <button type="button" className="btn-primary" disabled aria-disabled="true"
        style={{ fontSize: 10, opacity: 0.5, cursor: 'not-allowed', width: '100%' }}>
        Checkout not available yet
      </button>
    </div>
  );

  return (
    <div role="alert" style={{ maxWidth: 640, margin: '0 auto', textAlign: 'left' }}>
      <div style={{ ...card, borderColor: 'rgba(255,209,102,0.45)', marginBottom: '1rem' }}>
        <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 17, color: '#ffd166', fontWeight: 700, marginBottom: 6 }}>
          Analysis quota used up
        </div>
        <div style={{ ...mono, fontSize: 10, color: '#a0c4d8' }}>
          {message || 'Your free monthly analyses are used up and you have no credits or active subscription.'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {option('Buy one analysis', singlePrice,
          'One extra analysis credit. No subscription required.', 'option-single')}
        {option('Monthly plan', monthlyPrice,
          monthly && monthly.analysis_limit != null
            ? `${monthly.analysis_limit} analyses every ${monthly.period_days} days.`
            : 'Higher monthly analysis allowance.', 'option-monthly')}
      </div>
      <p style={{ ...mono, fontSize: 9, color: '#6b8fa8', marginTop: '1rem' }}>
        Online payments are not enabled yet, so nothing can be purchased and you will not be charged.
        {plans.status === 'error' && ' Prices are currently unavailable.'}
        {' '}Your free quota resets at the start of next month (UTC).
      </p>
      <button type="button" className="btn-ghost" style={{ fontSize: 10, marginTop: '0.5rem' }} onClick={onBack}>
        Back
      </button>
    </div>
  );
}

/* ── Composite panel shown under the upload zone ─────────────────────────── */
export default function AccountPanel({ refreshKey, onOpenJob }) {
  return (
    <div className="arise-account-panel" style={{
      maxWidth: 960, margin: '2rem auto 0', display: 'grid', gap: '1rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    }}>
      <PlanSummary refreshKey={refreshKey} />
      <JobHistory refreshKey={refreshKey} onOpen={onOpenJob} />
    </div>
  );
}
